import { useEffect } from 'react';
import { isScheduledPath, isSnoozedPath } from '@/features/scheduling/route';
import { LinkConfirm } from '@/features/message-frame/IsolatedMessageFrame';
import { LazyAccountSheet } from '@/components/AccountSwitcher';
import { InstallPrompt } from '@/components/InstallPromptGate';
import { DndSlot } from '@/components/DndIndicatorGate';
import {
  AddToTaskDialog,
  AiSummaryPanel,
  BlockSenderDialog,
  ComposeDialog,
  ContactEditor,
  FolderDialog,
  LabelDialog,
  LabelPicker,
  MessageInviteCard,
  ShortcutSheet,
  SnoozeDialog,
} from './lazyUi';
import {
  AdminView,
  CalendarView,
  ContactsView,
  SETTINGS_SECTIONS,
  ScheduledView,
  SettingsView,
  SnoozedView,
  TasksView,
} from './lazyRoutes';

/**
 * Prefetch, so lazy never feels lazy (docs/plan/06-frontend-v1.md §12, #208).
 *
 * Two triggers:
 *
 * - **Idle, after the first paint:** the chunks the mail view is most likely
 *   to need next, most likely first — compose (`c`), then what a message or a
 *   verb opens (Shift+S, invites, `l`, `b`, Shift+T, `?`). One per idle
 *   period, so none of it competes with the first paint or with input.
 * - **Intent:** pointer over, focus on, or a touch of a link to another view
 *   warms that view's chunk (and a settings link its section's), so a click
 *   ~100–300 ms later finds it loaded. Delegated from the document, so no
 *   link anywhere has to remember to opt in.
 *
 * Every chunk is also in the service worker's precache, so offline and repeat
 * visits read them from disk; this is about the first visit and about taking
 * even the disk read off the click's critical path.
 */

type Preload = () => Promise<unknown>;

/** Most likely next first. */
export const IDLE_PREFETCH: readonly Preload[] = [
  ComposeDialog.preload,
  AiSummaryPanel.preload,
  MessageInviteCard.preload,
  LabelPicker.preload,
  SnoozeDialog.preload,
  AddToTaskDialog.preload,
  ShortcutSheet.preload,
  BlockSenderDialog.preload,
  FolderDialog.preload,
  LabelDialog.preload,
  ContactEditor.preload,
  LazyAccountSheet.preload,
  LinkConfirm.preload,
  InstallPrompt.preload,
  DndSlot.preload,
];

/** The chunk(s) behind an in-app path, for intent prefetch; empty for mail. */
export function preloadsForPath(pathname: string): Preload[] {
  if (isScheduledPath(pathname)) return [ScheduledView.preload];
  if (isSnoozedPath(pathname)) return [SnoozedView.preload];
  const [first = '', second = ''] = pathname.replace(/^\/+/, '').split('/');
  switch (first) {
    case 'calendar':
      return [CalendarView.preload];
    case 'contacts':
      return [ContactsView.preload];
    case 'tasks':
      return [TasksView.preload];
    case 'admin':
      return [AdminView.preload];
    case 'settings': {
      const section = SETTINGS_SECTIONS[second];
      return section ? [SettingsView.preload, section.preload] : [SettingsView.preload];
    }
    default:
      return [];
  }
}

function saveData(): boolean {
  const connection = (navigator as { connection?: { saveData?: boolean } }).connection;
  return connection?.saveData === true;
}

type IdleWindow = {
  requestIdleCallback?: (callback: () => void, options?: { timeout: number }) => number;
  cancelIdleCallback?: (handle: number) => void;
};

/** Runs `task` in the next idle period (or after a short timeout where there is no such API). */
function whenIdle(task: () => void): () => void {
  const idle = window as IdleWindow;
  if (idle.requestIdleCallback) {
    const handle = idle.requestIdleCallback(task, { timeout: 2_000 });
    return () => idle.cancelIdleCallback?.(handle);
  }
  const handle = window.setTimeout(task, 200);
  return () => window.clearTimeout(handle);
}

/** Loads each of `preloads` in turn, one idle period apart. Returns a cancel. */
export function prefetchWhenIdle(preloads: readonly Preload[]): () => void {
  let cancelled = false;
  let cancelPending = () => {};
  const next = (index: number) => {
    if (cancelled || index >= preloads.length) return;
    cancelPending = whenIdle(() => {
      if (cancelled) return;
      // A failed prefetch is not an error anyone should see: the render that
      // needs the chunk tries again and reports if that fails too.
      preloads[index]!().catch(() => {}).finally(() => next(index + 1));
    });
  };
  const start = () => next(0);
  // After the load event, so none of this is in the first paint's way.
  if (document.readyState === 'complete') start();
  else window.addEventListener('load', start, { once: true });
  return () => {
    cancelled = true;
    cancelPending();
    window.removeEventListener('load', start);
  };
}

/** Warms a view's chunk when a link to it is pointed at, focused or touched. */
export function prefetchOnIntent(root: Document = document): () => void {
  const warmed = new Set<string>();
  const onIntent = (event: Event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;
    const link = target.closest<HTMLAnchorElement>('a[href]');
    if (!link || link.origin !== window.location.origin) return;
    const path = link.pathname;
    if (warmed.has(path)) return;
    warmed.add(path);
    for (const preload of preloadsForPath(path)) preload().catch(() => {});
  };
  const options = { capture: true, passive: true } as const;
  root.addEventListener('pointerover', onIntent, options);
  root.addEventListener('focusin', onIntent, options);
  root.addEventListener('touchstart', onIntent, options);
  return () => {
    root.removeEventListener('pointerover', onIntent, options);
    root.removeEventListener('focusin', onIntent, options);
    root.removeEventListener('touchstart', onIntent, options);
  };
}

/** Mounted once in the signed-in shell (router.tsx); renders nothing. */
export function IdlePrefetch() {
  useEffect(() => {
    const stopIntent = prefetchOnIntent();
    // Save-Data: intent only, no speculative downloads.
    const stopIdle = saveData() ? () => {} : prefetchWhenIdle(IDLE_PREFETCH);
    return () => {
      stopIntent();
      stopIdle();
    };
  }, []);
  return null;
}
